import type { AuthenticationResult, IPublicClientApplication } from "@azure/msal-browser";

import { routes } from "../config/routes";
import { getAssignedDataGroup, setAccessToken, setIdToken, setUserRights } from "./SecurityManager";

export const MS_ENTRA_LOGIN_ERROR_KEY = "msEntraLoginError";

const fetchUserRightsByDataGroup = async (baseUrl: string, idToken: string, dataGroupCode: string) => {
    const response = await fetch(`${baseUrl}switch-data-group?dataGroupCode=${encodeURIComponent(dataGroupCode)}`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
        },
    });
    if (!response.ok) throw new Error("Failed to fetch user rights by data group");
    return response.json();
};

const fetchUserRightsByRoles = async (baseUrl: string, idToken: string, roles: string[]) => {
    const response = await fetch(`${baseUrl}user-rights`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ userRoles: roles }),
    });
    if (!response.ok) throw new Error("Failed to fetch user rights by roles");
    return response.json();
};

const finishLogin = async (instance: IPublicClientApplication, loginResponse: AuthenticationResult, baseUrl: string) => {
    if (!loginResponse.accessToken) {
        throw new Error("Login failed: No access token received");
    }

    instance.setActiveAccount(loginResponse.account);

    const { accessToken, idToken, idTokenClaims } = loginResponse;
    const claims = idTokenClaims as { roles?: string[] };
    let userRights: string[] = [];

    const dataGroup = getAssignedDataGroup();

    // IF there is already a DG assigned, get the rights assigned to the DG, not the user.
    if (dataGroup) {
        const data = await fetchUserRightsByDataGroup(baseUrl, idToken, dataGroup.code ?? "");
        userRights = data.userRights;
    } else if (claims.roles && claims.roles.length > 0) {
        const data = await fetchUserRightsByRoles(baseUrl, idToken, claims.roles);
        userRights = data.userRights;
    }

    setUserRights(userRights);
    setAccessToken(accessToken);
    setIdToken(idToken);
};

/**
 * Processes the response MSAL produces once the browser returns from `loginRedirect` (see
 * ApaMSELoginPage). Unlike `loginPopup`, a redirect is a full-page navigation, so there's no
 * component still mounted to receive the result — this must run once at app bootstrap, before
 * the router's route loaders decide whether to bounce the user to /login (see msalConfig.ts).
 */
export const handleMsEntraLoginRedirect = async (instance: IPublicClientApplication, baseUrl: string) => {
    try {
        const redirectResult = await instance.handleRedirectPromise();
        if (!redirectResult) {
            return;
        }

        await finishLogin(instance, redirectResult, baseUrl);
    } catch (err) {
        // The login page itself isn't mounted anymore at this point, so hand the error back to it.
        sessionStorage.setItem(MS_ENTRA_LOGIN_ERROR_KEY, (err as Error)?.message || "Microsoft login failed");
        window.location.replace(routes.login.path);
    }
};
